// Rated Orders — complaint case model shared by the queue page, the quick-actions
// panel and the order modal. Mirrors functions/ratedOrders (RATED_ORDERS_PLAN.md §3.1).
import { firestore } from '../../../../helpers/Firebase';
import { ORDER_COLLECTION } from '../../../../config/env';
import { accessTypesObject } from '../../../../helpers/Utils';
import { sendApologyIfDue } from './apologyPush';

export const COMPLAINT_STATUS = {
  pending: { label: 'Pending', color: 'danger', hint: 'Nobody has looked at it yet' },
  inProgress: { label: 'In Progress', color: 'warning', hint: 'Support is working on it' },
  open: { label: 'Open', color: 'info', hint: 'Waiting on the customer or the provider' },
  resolved: { label: 'Resolved', color: 'success', hint: 'Closed with a resolution' },
};
export const COMPLAINT_STATUS_ORDER = ['pending', 'inProgress', 'open', 'resolved'];
export const OPEN_COMPLAINT_STATUSES = ['pending', 'inProgress', 'open'];
export const RESOLUTION_TYPES = [
  { value: 'compensated', label: 'Compensated (wallet)' },
  { value: 'voucher', label: 'Voucher issued' },
  { value: 'explained', label: 'Explained / apologised' },
  { value: 'providerWarned', label: 'Provider warned' },
  { value: 'noAction', label: 'No action needed' },
  { value: 'other', label: 'Other' },
];
// react-select styles for selects whose options carry their own colour (pills): light
// selected / hover rows instead of the default solid blue, which swallows the pill and hint.
// Menus render in place (no menuPortalTarget): the app sets `zoom: 0.9` on <html>, which throws
// react-select's body-portal coordinates off, so a portalled menu lands up and left of its field.
export const SOFT_SELECT_STYLES = {
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#eef3f8' : state.isFocused ? '#f6f8fa' : '#fff',
    color: '#3a3a3a',
    cursor: 'pointer',
    ':active': { backgroundColor: '#e4ebf3' },
  }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
};
export const PILL = { fontSize: '11px', padding: '4px 10px', fontWeight: 600, letterSpacing: '0.2px' };

export const RATED_ORDERS_ROLES = ['superAdmin', 'customerService'];
export const canManageComplaints = (user) => RATED_ORDERS_ROLES.includes(user?.accessType);
// Providers ('admin') see the queue read-only, scoped to their own bookings (sp_id == uid).
export const RATED_ORDERS_VIEW_ROLES = [...RATED_ORDERS_ROLES, 'admin'];
export const canViewRatedOrders = (user) => RATED_ORDERS_VIEW_ROLES.includes(user?.accessType);
export const isProviderUser = (user) => user?.accessType === 'admin';

const actor = (user) => ({
  createdBy: accessTypesObject?.[user?.accessType]?.name || user?.accessType || 'Staff',
  createdByName: user?.displayName || user?.name || user?.email || '',
});

const appendLog = (bookingId, entry) => firestore().collection('logs').doc(bookingId).set({
  data: firestore.FieldValue.arrayUnion(entry),
  updated: Date.now(),
}, { merge: true });

/**
 * Move a complaint to a new status. Resolution fields are required for 'resolved'.
 * Writes bookings/{id}.complaint.* + complaintOpen and appends a log entry.
 */
export async function setComplaintStatus({ booking, status, user, note = '', resolutionType = '', assignToMe = true }) {
  if (!COMPLAINT_STATUS[status]) throw new Error(`Unknown complaint status ${status}`);
  const id = booking.key || booking.id || booking.bookingId;
  const now = Date.now();
  const who = actor(user);
  const current = booking.complaint || {};
  const oldStatus = current.status || 'pending';
  const update = {
    'complaint.status': status,
    'complaint.lastActionAt': now,
    'complaint.lastActionBy': who.createdByName || who.createdBy,
    complaintOpen: OPEN_COMPLAINT_STATUSES.includes(status),
    updated: now,
  };
  if (status !== 'pending' && assignToMe && !current.assigneeUid) {
    update['complaint.assigneeUid'] = user?.uid || '';
    update['complaint.assigneeName'] = who.createdByName || who.createdBy;
    update['complaint.assignedAt'] = now;
  }
  let logEntry;
  if (status === 'resolved') {
    if (!resolutionType) throw new Error('Pick a resolution type');
    update['complaint.resolvedAt'] = now;
    update['complaint.resolvedBy'] = who.createdByName || who.createdBy;
    update['complaint.resolvedByUid'] = user?.uid || '';
    update['complaint.resolutionType'] = resolutionType;
    update['complaint.resolutionNote'] = String(note || '').trim();
    logEntry = { title: 'complaintResolved', created: now, ...who, resolutionType, resolutionNote: String(note || '').trim() };
  } else {
    if (oldStatus === 'resolved') update['complaint.reopenedCount'] = Number(current.reopenedCount || 0) + 1;
    logEntry = { title: 'complaintStatusChanged', created: now, ...who, oldStatus, status, note: String(note || '').trim(), assigneeName: update['complaint.assigneeName'] || current.assigneeName || '' };
  }
  // Customer apology push: once, the first time the case moves to In Progress (gated by settings).
  let apology = null;
  if (status === 'inProgress' && !current.apologySentAt) {
    apology = await sendApologyIfDue({ booking: { ...booking, key: id } });
    if (apology.sent) update['complaint.apologySentAt'] = apology.at;
  }
  await firestore().collection(ORDER_COLLECTION).doc(id).update(update);
  await appendLog(id, logEntry).catch((e) => console.error('complaint log failed', e));
  if (apology && apology.sent) {
    await appendLog(id, { title: 'apologySent', created: now + 1, ...who }).catch((e) => console.error('apology log failed', e));
  }
  return update;
}

export const complaintAgeHours = (booking, now = Date.now()) => {
  const t = Number(booking?.complaint?.detectedAt) || Number(booking?.rated) || 0;
  return t ? (now - t) / 3600e3 : 0;
};
